import type { FormTourConfig } from './formTour';

const ADD_CANDIDATE_TO_SEQUENCE_START_PATH = '/sequences';
const SEQUENCES_PATH_PREFIX = '/sequences';
const SEQUENCE_RECIPIENT_ROW_TEST_ID = '@sequence-recipients-row';

export const addCandidateToSequenceTour = {
  taskId: 'add_candidate_to_sequence',
  storageKey: 'onboarding-add-candidate-to-sequence-tour-step',
  startPath: ADD_CANDIDATE_TO_SEQUENCE_START_PATH,
  activePathPrefix: SEQUENCES_PATH_PREFIX,
  navStepCount: 1,
  overlineLabel: 'Add Candidate to Sequence Tour',
  steps: [
    {
      title: 'Open Sequences',
      description: 'Click Sequences in the sidebar to view your outreach sequences.',
      targetTestIds: ['@sidebar-sequences'],
    },
    {
      title: 'Show draft sequences',
      description:
        'New sequences start as draft. Click the Draft filter to find the sequence you just created.',
      targetTestIds: ['@sequences-filter-draft'],
      disableBack: true,
    },
    {
      title: 'Select Sequence',
      description:
        'In the draft list, click the sequence name to open the sequence you created.',
      targetTestIds: ['@sequence-open-link'],
      disableBack: true,
    },
    {
      title: 'Open Recipients',
      description: 'Open the Recipients tab to manage who is enrolled in this sequence.',
      targetTestIds: ['@sequence-tab-recipients'],
      disableBack: true,
    },
    {
      title: 'Add recipients',
      description: 'Click Add Recipients to choose candidates for this sequence.',
      targetTestIds: ['@sequence-add-recipients'],
      closeTargetTestIdsOnBack: ['@sequence-add-recipients-modal'],
    },
    {
      title: 'Select Candidate',
      description: 'Select one or more candidates from the list using the checkboxes.',
      targetTestIds: ['@sequence-add-recipients-picker'],
      contextTargetTestIds: ['@sequence-add-recipients-modal'],
      panelPlacement: 'above',
      closeTargetTestIdsOnBack: ['@sequence-add-recipients-modal'],
    },
    {
      title: 'Add to sequence',
      description: 'Click Add to Sequence to continue. If you selected one candidate, a customization form will open next.',
      targetTestIds: ['@sequence-add-to-sequence'],
      contextTargetTestIds: ['@sequence-add-recipients-modal'],
      panelPlacement: 'above',
      closeTargetTestIdsOnBack: ['@sequence-customize-modal', '@sequence-add-recipients-modal'],
    },
    {
      title: 'Add the candidate',
      description:
        'Review the email steps if you like, then click Add to add this candidate to the sequence. The guide finishes when they appear in the recipients list.',
      targetTestIds: ['@sequence-customize-add'],
      highlightTargetTestIds: ['@sequence-customize-add'],
      contextTargetTestIds: ['@sequence-customize-modal'],
      panelPlacement: 'above',
      closeTargetTestIdsOnBack: ['@sequence-customize-modal'],
    },
  ],
  stepActions: {
    0: {
      type: 'wait-for-target',
      advanceTo: 1,
      waitForTargetTestIds: ['@sequences-filter-draft'],
    },
    1: {
      type: 'wait-for-target',
      advanceTo: 2,
      waitForTargetTestIds: ['@sequences-filter-draft-active'],
    },
    2: {
      type: 'wait-for-target',
      advanceTo: 3,
      waitForTargetTestIds: ['@sequence-name-edit'],
    },
    3: {
      type: 'wait-for-target',
      advanceTo: 4,
      waitForTargetTestIds: ['@sequence-recipients-panel'],
    },
    4: {
      type: 'wait-for-target',
      advanceTo: 5,
      waitForTargetTestIds: ['@sequence-add-recipients-modal'],
      captureRowCountBaselineTestIds: [SEQUENCE_RECIPIENT_ROW_TEST_ID],
    },
    5: {
      type: 'wait-for-target',
      advanceTo: 6,
      waitForTargetTestIds: ['@sequence-add-to-sequence'],
    },
    6: {
      type: 'wait-for-target-or-complete',
      advanceTo: 7,
      waitForTargetTestIds: ['@sequence-customize-modal'],
      completeWhenTargetTestIds: [SEQUENCE_RECIPIENT_ROW_TEST_ID],
      completeWhenCountIncreases: true,
    },
    7: {
      type: 'complete-when-target',
      waitForTargetTestIds: [SEQUENCE_RECIPIENT_ROW_TEST_ID],
      completeWhenCountIncreases: true,
    },
  },
} as const satisfies FormTourConfig;
